import React, { useState, useEffect, CSSProperties } from "react";
import { TextAreaValidationRules } from "types";

interface TextAreaProps {
  label: string;
  value: string;
  name: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  rules?: TextAreaValidationRules;
  className?: string;
  style?: CSSProperties;
  error?: string;
}

const TextArea: React.FC<TextAreaProps> = ({
  label,
  value,
  name,
  onChange,
  placeholder = "Enter text here...",
  rules = {},
  className = "",
  style = {},
  error: externalError,
}) => {
  const [error, setError] = useState<string | null>(null);

  const validate = (val: string) => {
    if (rules.required) {
      const msg =
        typeof rules.required === "string"
          ? rules.required
          : `${label} is required`;
      if (!val.trim()) {
        return msg;
      }
    }

    if (rules.minLength) {
      if (val.length < rules.minLength.value) {
        return (
          rules.minLength.message ||
          `${label} must be at least ${rules.minLength.value} characters`
        );
      }
    }

    if (rules.maxLength) {
      if (val.length > rules.maxLength.value) {
        return (
          rules.maxLength.message ||
          `${label} must be at most ${rules.maxLength.value} characters`
        );
      }
    }

    if (rules.pattern) {
      if (!rules.pattern.value.test(val)) {
        return rules.pattern.message || `${label} format is invalid`;
      }
    }

    if (rules.validate) {
      const result = rules.validate(val);
      if (result !== true) {
        return typeof result === "string" ? result : `${label} is invalid`;
      }
    }

    return null;
  };

  useEffect(() => {
    setError(validate(value));
  }, [value]);

  const displayError = externalError || error;

  return (
    <div>
      <label
        htmlFor={name}
        className="block mb-2 text-sm font-medium text-[#747171]"
      >
        {label}
      </label>
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={4}
        aria-invalid={displayError ? "true" : "false"}
        aria-describedby={displayError ? `${name}-error` : undefined}
        className={`text-sm rounded-lg block w-full p-2.5 mb-4
          text-gray-900 dark:placeholder-gray-400
          focus:outline-none
          ${displayError ? "border-red-500" : "border-none"}
          ${className}`.trim()}
        style={{ backgroundColor: "#F2F2F2", border: "none", ...style }}
        required={!!rules.required}
      />
      {displayError && (
        <p
          id={`${name}-error`}
          className="mt-1 text-sm text-red-600 dark:text-red-500"
        >
          {displayError}
        </p>
      )}
    </div>
  );
};

export default TextArea;

